import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Prop, State, Watch } from "@stencil/core";
import { QuestionRadio } from "./question-radio";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import { Answers, questions, Question, firstQuestionKey, buildRequestData } from "../../api/publicApi/proactive-case-closure";
import { QuestionComment } from "./question-comment";
import { getPublicApiClient, PublicApiClient } from "../../api/publicApi/PublicApiClient";
import { authClientName, EnvConfig, getEnvConfig, setEnv } from "../../api/config";

const baseClass = "mf-proactive-case-closure";
/**
 * Micro-frontend component for the proactive case closure form
 * This form shows a series of questions to the user and collects the answers
 * depending on the answers the next question is displayed
 */
@Component({
    tag: "mf-proactive-case-closure",
    styleUrl: "proactive-case-closure.css",
    shadow: false
})
export class ProactiveCaseClosure {
    @Element() el: HTMLMfProactiveCaseClosureElement;

    /** SIS case ID */
    @Prop() callId: string = "";
    /**
     * The locale to use for translations
     */
    @Prop({ reflect: true }) locale: string = "en-US";
    /**
     * Indicates if the component is used in an app context
     */
    @Prop() isApp: boolean = false;
    /**
     * Environment string to decide which endpoints to use
     */
    @Prop() env: string = "prod";

    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;

    @State() questions: Question[] = [];

    /**
     * only for temporary settings while debugging
     * @deprecated do not use in production
     */
    @Prop() debug: boolean = false;

    @State() answers: Answers = {};
    /** indicates if all required answers were given */
    @State() complete: boolean = false;
    @State() submitting: boolean = false;
    @State() submitSuccess: boolean = false;
    @State() submitError: string | null = null;
    @State() authError: string | null = null;
    @State() config: EnvConfig | null = null;
    @State() loadingTranslations = true;
    @State() isAuthenticating = false;

    private submitButton: HTMLElement | null = null;
    private unsubscribeAuth: () => void;
    private apiClient: PublicApiClient | null = null;

    /**
     * Event that is fired when the user clicks the cancel button
     */
    @Event({
        eventName: "mfServiceCallCancel",
        bubbles: true,
        composed: true
    })
    cancelEvent: EventEmitter<void>;

    /**
     * Event that is fired when the user clicks the final OK button
     */
    @Event({
        eventName: "mfServiceCallSuccess",
        bubbles: true,
        composed: true
    })
    successEvent: EventEmitter<void>;

    /**
     * Event that is fired when the success message is shown
     * (this is used in the modal to trigger a reload after the modal is closed via X, only when the massage has been shown)
     */
    @Event({
        eventName: "mfServiceCallShowSuccess",
        bubbles: true,
        composed: true
    })
    showSuccessEvent: EventEmitter<void>;

    private i18nSubscriberId: string;
    private unsubscribeToTbdbToken: () => void;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.setEnv();
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
        }
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }

    componentWillLoad() {
        if (!(this.isApp || this.debug)) {
            this.isAuthenticating = true;
        }
        this.loadTranslations();
        this.updateQuestions();
        this.checkComplete();
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Watch("env")
    private setEnv() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.apiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.apiClient.subscribeToTbdbToken((authEvent) => {
            if (authEvent.errorMessage) {
                this.authError = authEvent.errorMessage;
            }
            this.isAuthenticating = false;
        });
    }

    @Watch("submitSuccess")
    submitSuccessChanged() {
        if (this.submitSuccess) {
            this.showSuccessEvent.emit();
        }
    }

    private loadTranslations() {
        this.loadingTranslations = true;
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
            this.loadingTranslations = false;
        });
    }

    private submit() {
        const data = buildRequestData(this.answers, this.callId);
        if (this.isApp) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((window as any).SendRawMessage as any)({ type: "proactiveCaseClosure", data });
            return;
        }
        if (!this.apiClient) {
            this.submitError = "No API client available";
            return;
        }
        this.submitting = true;
        this.apiClient
            .request("/servicecall/proactivecaseclosure", { body: data })
            .then((response) => {
                if (response) {
                    this.submitSuccess = true;
                } else {
                    this.submitError = "An error occurred while submitting the form";
                }
                this.submitting = false;
            })
            .catch((error) => {
                this.submitError = error || "Unknown error";
                this.submitting = false;
            });
    }

    private cancel() {
        this.cancelEvent.emit();
    }

    private finish() {
        this.successEvent.emit();
    }

    /**
     * this method collects the questions that can be displayed
     * We start at the initial question and get the next question based on the selected answer
     */
    @Watch("answers")
    private updateQuestions(): void {
        const questionsArray: Question[] = [questions[firstQuestionKey]];
        while (this.hasAnsweredLast(questionsArray) && this.lastAnswerHasNext(questionsArray)) {
            questionsArray.push(this.getNextQuestion(questionsArray[questionsArray.length - 1]));
        }
        this.updateAnswers(questionsArray);
        this.questions = questionsArray;
    }

    @Watch("answers")
    @Watch("questions")
    private checkComplete(): void {
        this.complete =
            this.hasAnsweredLast(this.questions) &&
            !this.lastAnswerHasNext(this.questions) &&
            this.hasAnsweredComment(this.questions);
    }

    @Watch("questions")
    onQuestionsChange() {
        if (this.complete) return;
        setTimeout(() => {
            this.el.querySelector(`.${baseClass}__question-container:last-child`)?.scrollIntoView({ behavior: "smooth" });
        }, 200);
    }

    @Watch("complete")
    onCompletionChange(): void {
        if (this.complete) {
            setTimeout(() => {
                this.submitButton?.scrollIntoView({ behavior: "smooth" });
            }, 200);
        }
    }

    private hasAnsweredLast(questionsArray: Question[]): boolean {
        return this.answers[questionsArray[questionsArray.length - 1].name] !== undefined;
    }

    private lastAnswerHasNext(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        return lastQuestion.options[this.answers[lastQuestion.name]].next !== undefined;
    }

    private getNextQuestion(currentQuestion: Question): Question {
        const currentAnswer = this.answers[currentQuestion.name] as string;
        return questions[currentQuestion.options[currentAnswer].next as string];
    }

    private hasAnsweredComment(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        if (!lastQuestion.comment) {
            return true;
        }
        const commentAnswer = this.answers[lastQuestion.comment.name];
        return commentAnswer && commentAnswer.trim() !== "";
    }

    /** this method removes those answers where the associated question has been removed */
    private updateAnswers(questionsArray: Question[]): void {
        const answersObject = { ...this.answers };
        Object.keys(answersObject).forEach((key) => {
            if (
                !questionsArray.find((question) => question.name === key) &&
                !questionsArray.find((question) => question.comment?.name === key)
            ) {
                delete answersObject[key];
            }
        });
        if (JSON.stringify(answersObject) !== JSON.stringify(this.answers)) {
            this.answers = answersObject;
        }
    }

    private onSilentAuthError = (e: CustomEvent): void => {
        if (this.isApp || this.debug) {
            return;
        }
        this.authError = e.detail.error;
        if (this.authError !== null) {
            this.isAuthenticating = false;
        }
    };

    render() {
        const t = (key: TranslationKey, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const isLoading = this.loadingTranslations || this.isAuthenticating;

        if (!this.isApp && this.callId === "") {
            return <Host data-error="no callId provided" />;
        }

        if (this.config === null) {
            return <Host data-error="no config" />;
        }

        const silentAuth = !(this.isApp || this.debug) && (
            <ux-u-silent-auth
                clientName={authClientName}
                clientId={this.config?.authClientId}
                issuer={this.config?.authIssuer}
                debug={this.config?.logging}
                scope={this.config?.authScope}
                onUxAuth={(e: CustomEvent) => this.onSilentAuthError(e)}
            />
        );

        if (isLoading) {
            return (
                <Host class={baseClass}>
                    {silentAuth}
                    <div class={`${baseClass}__loader`}>
                        <ux-loader variant="spinner"></ux-loader>
                    </div>
                </Host>
            );
        }

        if (this.submitSuccess) {
            return (
                <Host class={baseClass}>
                    {silentAuth}
                    <div class={`${baseClass}__container`}>
                        <div class="ux-u-text-align--center ux-u-mb-xxs">
                            <ux-icon class={`${baseClass}__result-icon ux-color-success`} name="checkmark" weight="light"></ux-icon>
                        </div>
                        <h1 class="ux-text-headline ux-text-headline--big ux-u-text-align--center">
                            {t("serviceCase.proactiveCaseClosure.dialogTitle")}
                        </h1>
                        <div class="ux-u-text-align--center">
                            <ux-button onClick={() => this.finish()} variant="primary">
                                {t("serviceCase.proactiveCaseClosure.dialog.buttonOk")}
                            </ux-button>
                        </div>
                    </div>
                </Host>
            );
        }

        if (!this.isApp && this.authError) {
            return (
                <Host class={baseClass}>
                    {silentAuth}
                    <div class={`${baseClass}__container`}>
                        <div class="ux-u-text-align--center ux-u-mb-xxs">
                            <ux-icon class={`${baseClass}__result-icon ux-color-error`} name="cross" weight="light"></ux-icon>
                        </div>
                        <h1 class="ux-text-headline ux-text-headline--big ux-u-text-align--center">Error: Not authenticated</h1>
                        <p class="ux-text-normal ux-u-text-align--center ux-u-margin-vertical-20">{this.authError}</p>
                        <div class="ux-u-text-align--center">
                            <ux-button onClick={() => this.cancel()}>{t("button.cancel")}</ux-button>
                        </div>
                    </div>
                </Host>
            );
        }

        return (
            <Host class={baseClass}>
                {silentAuth}
                <div class={`${baseClass}__container`}>
                    {!this.isApp && (
                        <h1 class="ux-text-headline ux-text-headline--big ux-u-text-align--center">
                            {t("serviceCase.proactiveCaseClosure.title")}
                        </h1>
                    )}
                    <p class={`${baseClass}__introduction ux-text-normal`}>{t("serviceCase.proactiveCaseClosure.instructions")}</p>
                    {this.questions.map((question) => (
                        <div class={`${baseClass}__question-container ux-u-shadow ux-u-background-white ux-u-margin-vertical-20`}>
                            <QuestionRadio
                                t={t}
                                question={question}
                                answers={this.answers}
                                setAnswers={(answers) => {
                                    this.answers = answers;
                                }}
                                baseClass={baseClass}
                            />
                            {question.comment && (
                                <QuestionComment
                                    label={t(question.comment.labelKey as TranslationKey) as unknown as string}
                                    placeholder={t(question.comment.placeholderKey as TranslationKey) as unknown as string}
                                    name={question.comment.name}
                                    answers={this.answers}
                                    setAnswers={(answers) => {
                                        this.answers = answers;
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {this.submitError && <p class={`${baseClass}__error ux-text-normal`}>{this.submitError}</p>}
                </div>
                <div class={`${baseClass}__button-container`}>
                    {!this.isApp && (
                        <ux-button onClick={() => this.cancel()} class={`${baseClass}__cancel-button`}>
                            {t("button.cancel")}
                        </ux-button>
                    )}
                    <ux-button
                        onClick={() => this.submit()}
                        ref={(el: HTMLElement) => (this.submitButton = el)}
                        class={`${baseClass}__submit-button`}
                        variant="primary"
                        disabled={!this.complete || this.submitting}
                    >
                        {t("serviceCase.proactiveCaseClosure.question.submit")}
                    </ux-button>
                </div>
            </Host>
        );
    }
}

export interface ProactiveCaseClosureProps {
    /** SIS case ID */
    callId: string;
    /**
     * The locale to use for translations
     */
    locale: string;
    /**
     * Indicates if the component is used in an app context
     */
    isApp: boolean;
    /**
     * Environment string to decide which endpoints to use
     */
    env: string;
    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    legacyApi: boolean;
    /**
     * only for temporary settings while debugging
     * @deprecated do not use in production
     */
    debug: boolean;
}
